import { getFirestore, doc, updateDoc } from "firebase/firestore";
import { app } from "../firebase";

const db = getFirestore(app);

export const TUTORIAL_STATUS = {
	PENDING: "pending",
	IN_PROGRESS: "in_progress",
	COMPLETED: "completed",
	SKIPPED: "skipped",
};

/** User-document fields owned by onboarding. Other writers must never overwrite them with stale copies. */
export const ONBOARDING_FIELDS = ["spotlightTutorial", "referral", "referralPending"];

const nowIso = () => new Date().toISOString();

export const omitOnboardingFields = (userData) => {
	if (!userData) return userData;
	const rest = { ...userData };
	ONBOARDING_FIELDS.forEach((field) => delete rest[field]);
	return rest;
};

export const pickOnboardingFields = (userData) => {
	const picked = {};
	ONBOARDING_FIELDS.forEach((field) => {
		if (userData && userData[field] !== undefined) picked[field] = userData[field];
	});
	return picked;
};

/** Shallow comparison that ignores onboarding-owned fields. */
export const isSameExceptOnboarding = (previous, next) => {
	if (previous === next) return true;
	if (!previous || !next) return false;
	const previousFields = omitOnboardingFields(previous);
	const nextFields = omitOnboardingFields(next);
	const previousKeys = Object.keys(previousFields);
	if (previousKeys.length !== Object.keys(nextFields).length) return false;
	return previousKeys.every((key) => previousFields[key] === nextFields[key]);
};

export const createPendingTutorialState = () => ({
	status: TUTORIAL_STATUS.PENDING,
	completedChapters: [],
	createdAt: nowIso(),
	updatedAt: nowIso(),
});

export const createStartedTutorialState = ({ chapterId, stepId, completedChapters = [], previous = {}, extra = {} }) => ({
	status: TUTORIAL_STATUS.IN_PROGRESS,
	currentChapterId: chapterId,
	currentStepId: stepId,
	completedChapters,
	startedAt: previous.startedAt || nowIso(),
	createdAt: previous.createdAt || nowIso(),
	updatedAt: nowIso(),
	...extra,
});

/** A reset user is eligible again: the tutorial launches the next time they reach My Profile. */
export const createResetTutorialState = (extra = {}) => ({
	...createPendingTutorialState(),
	...extra,
});

export const withTutorialPosition = (state, { chapterId, stepId, completedChapters }) => ({
	...state,
	status: TUTORIAL_STATUS.IN_PROGRESS,
	currentChapterId: chapterId,
	currentStepId: stepId,
	completedChapters: completedChapters || state.completedChapters || [],
	autoNavigate: false,
	updatedAt: nowIso(),
});

export const withTutorialFinished = (state, status, extra = {}) => ({
	...state,
	status,
	autoNavigate: false,
	updatedAt: nowIso(),
	...(status === TUTORIAL_STATUS.COMPLETED ? { completedAt: nowIso() } : { skippedAt: nowIso() }),
	...extra,
});

const removeUndefinedValues = (value) => JSON.parse(JSON.stringify(value));

export const saveTutorialState = (email, tutorialState) =>
	updateDoc(doc(db, "users", email), { spotlightTutorial: removeUndefinedValues(tutorialState) });

/** Admin: write onboarding fields for any user. Never touches profile, Stripe, Registry or Event data. */
export const saveOnboardingFields = (userDocId, fields) => {
	const onboardingOnly = pickOnboardingFields(fields);
	return updateDoc(doc(db, "users", userDocId), removeUndefinedValues(onboardingOnly));
};

export const isTutorialUnfinished = (tutorialState) =>
	tutorialState?.status === TUTORIAL_STATUS.PENDING || tutorialState?.status === TUTORIAL_STATUS.IN_PROGRESS;
